import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { noop } from '../lib/serviceHelpers';

/**
 * Fetch the current provider's own profile
 */
export const useFetchOwnProfile = () => {
  return useQuery(
    ['provider-own-profile'],
    async () => {
      const res = await axios.get('/provider/profile');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

/**
 * Update the current provider's own profile
 */
export const useUpdateOwnProfile = (callback, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload) => {
      const res = await axios.put('/provider/profile', payload);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        // Invalidate the profile cache to refetch fresh data
        queryClient.invalidateQueries(['provider-own-profile']);
        if (callback) callback(data);
      },
    },
  );
};

/**
 * Upload the current provider's own signature
 */
export const useUploadOwnSignature = (callback, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (file) => {
      const formData = new FormData();
      formData.append('signature', file);
      const res = await axios.post('/provider/signature', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        // Invalidate the profile cache to refetch fresh data
        queryClient.invalidateQueries(['provider-own-profile']);
        if (callback) callback(data);
      },
    },
  );
};

/**
 * Upload the current provider's own letterhead
 */
export const useUploadOwnLetterhead = (callback, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (file) => {
      const formData = new FormData();
      formData.append('letterhead', file);
      const res = await axios.post('/provider/letterhead', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        // Invalidate the profile cache to refetch fresh data
        queryClient.invalidateQueries(['provider-own-profile']);
        if (callback) callback(data);
      },
    },
  );
};

// ============================================================================
// CERTIFICATION PROFILE HOOKS
// ============================================================================

/**
 * Fetch all certification profiles for the current provider
 */
export const useFetchProfiles = () => {
  return useQuery(
    ['provider-profiles'],
    async () => {
      const res = await axios.get('/provider/profiles');
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  );
};

/**
 * Create a new certification profile
 */
export const useCreateProfile = (callback = noop, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload) => {
      const res = await axios.post('/provider/profiles', payload);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['provider-profiles']);
        queryClient.invalidateQueries(['provider-own-profile']);
        callback(data);
      },
    },
  );
};

/**
 * Update an existing certification profile
 */
export const useUpdateProfile = (callback = noop, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ profileId, payload }) => {
      const res = await axios.put(`/provider/profiles/${profileId}`, payload);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['provider-profiles']);
        queryClient.invalidateQueries(['provider-own-profile']);
        callback(data);
      },
    },
  );
};

/**
 * Delete a certification profile
 */
export const useDeleteProfile = (callback = noop, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (profileId) => {
      const res = await axios.delete(`/provider/profiles/${profileId}`);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['provider-profiles']);
        queryClient.invalidateQueries(['provider-own-profile']);
        callback(data);
      },
    },
  );
};

/**
 * Set a certification profile as the default
 */
export const useSetDefaultProfile = (callback = noop, opts = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    async (profileId) => {
      const res = await axios.put(`/provider/profiles/${profileId}/default`);
      return res.data;
    },
    {
      ...opts,
      onSuccess: (data) => {
        queryClient.invalidateQueries(['provider-profiles']);
        queryClient.invalidateQueries(['provider-own-profile']);
        callback(data);
      },
    },
  );
};
